import { useContext, useState } from "react";
import { PrintContext } from "../context/PrintProvider/PrintProvider";
import usePopup from "./usePopup";

const spreadsheetDownloadTypes = new Set<string>(["xls", "xlsx", "ods"]);

function isSpreadsheetFileType(
  fileType?: keyof typeof allTypes,
): fileType is "xls" | "xlsx" | "ods" {
  return Boolean(fileType && spreadsheetDownloadTypes.has(fileType as string));
}

function spreadsheetFileName(fileType: keyof typeof allTypes): string {
  if (fileType === "xls") return "report.xls";
  if (fileType === "ods") return "report.ods";
  return "report.xlsx";
}

/** Opens or downloads the file; spreadsheets default to download (browser Excel preview is unreliable). */
function triggerSpreadsheetDownload(objectUrl: string, fileName: string) {
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch {
      /* ignore */
    }
  }, 500);
}

const usePrint = (): {
  printPdf: (file: Blob | string, type?: "new-tab" | "window",  fileType?:keyof typeof allTypes) => void;
  loading: boolean;
} => {
  const { iframeRef } = useContext(PrintContext);
  const { showSnackbar } = usePopup();
  const [loading, setLoading] = useState<boolean>(false);
  const printPdf = (file: Blob | string, type?: "new-tab" | "window",fileType?:keyof typeof allTypes) => {
    setLoading(true);
    if (!Boolean(iframeRef)) {
      throw new Error("usePrint must be used within an PrintProvider");
    }
    let blob: Blob = new Blob();
    let objectUrl = "";
    if (typeof file == "string") {
      if ((file as string).includes("blob:")) {
        objectUrl = file;
      } else {
        try {
          const byteCharacters = atob(file as string);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(null)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type: fileType ? allTypes[fileType] : "application/pdf" });
        } catch (error) {
          setLoading(false);
          showSnackbar({
            message: isSpreadsheetFileType(fileType)
              ? "Failed to prepare Excel file. Please try again."
              : "Failed to print pdf, please try again !",
            open: true,
            variant: "error",
            duration: 4000,
          });
          console.error(error);
          return;
        }
      }
    } else if (typeof file == "object") {
      blob = file;
    }
    try {
      if (!objectUrl) {
        objectUrl = URL.createObjectURL(blob);
      }

      if (isSpreadsheetFileType(fileType)) {
        const name = spreadsheetFileName(fileType);
        triggerSpreadsheetDownload(objectUrl, name);
        setLoading(false);
        return;
      }

      if (type == "new-tab") {
        setLoading(false);
        window.open(objectUrl, "_blank");
      } else {
        if (iframeRef.current) {
          iframeRef.current.src = objectUrl;
          iframeRef.current.onload = () => {
            setLoading(false);
            iframeRef.current?.contentWindow?.print();
          };
        } else {
          setLoading(false);
          console.warn("hook failed");
        }
      }
    } catch (error) {
      setLoading(false);
      showSnackbar({
        message: isSpreadsheetFileType(fileType)
          ? "Failed to open Excel file. Please try again."
          : "Failed to print pdf, please try again !",
        open: true,
        variant: "error",
        duration: 4000,
      });
      console.error(error);
    }
  };
  return { printPdf, loading };
};
export default usePrint;

const allTypes: { [key: string]: string } = {
  // Documents
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  rtf: "application/rtf",
  csv: "text/csv",
  odt: "application/vnd.oasis.opendocument.text",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  odp: "application/vnd.oasis.opendocument.presentation",

  // Images
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  bmp: "image/bmp",
  webp: "image/webp",
  tiff: "image/tiff",
  svg: "image/svg+xml",

  // Audio
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  flac: "audio/flac",
  aac: "audio/aac",
  wma: "audio/x-ms-wma",

  // Video
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  wmv: "video/x-ms-wmv",
  flv: "video/x-flv",
  mkv: "video/x-matroska",
  webm: "video/webm",
  mpeg: "video/mpeg",

  // Archives
  zip: "application/zip",
  rar: "application/vnd.rar",
  "7z": "application/x-7z-compressed",
  tar: "application/x-tar",
  gz: "application/gzip",

  // Code/Programming
  js: "application/javascript",
  ts: "application/typescript",
  json: "application/json",
  xml: "application/xml",
  html: "text/html",
  css: "text/css",
  py: "text/x-python",
  java: "text/x-java-source",
  cpp: "text/x-c++src",
  php: "application/x-httpd-php",

  // Miscellaneous
  ics: "text/calendar",
  vcf: "text/vcard",
  epub: "application/epub+zip",
  md: "text/markdown",
};
